import React, { useState } from 'react';
import LegalPageShell from './components/LegalPageShell';
import PageSeo from './seo/PageSeo';
import useFormSubmitGuard from './hooks/useFormSubmitGuard';
import {
  formatCpf,
  isValidCpf,
  isValidEmail,
} from './utils/patientContactValidation';

const BASE_URL = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/+$/, '');

const stripPhoneDigits = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  while (d.length > 11 && d.startsWith('55')) d = d.slice(2);
  if (d.startsWith('0') && (d.length === 11 || d.length === 12)) d = d.slice(1);
  return d.slice(0, 11);
};

const formatPhoneBR = (raw) => {
  const digits = stripPhoneDigits(raw);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const fieldStyle = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  marginTop: '0.35rem',
  padding: '0.75rem 0.85rem',
  borderRadius: '0.65rem',
  border: '1px solid rgba(0,0,0,0.12)',
  fontSize: '0.95rem',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.88rem',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '0.85rem',
};

const btnStyle = (disabled) => ({
  marginTop: '1.25rem',
  width: '100%',
  padding: '0.85rem 1rem',
  borderRadius: '0.75rem',
  border: 'none',
  background: disabled ? '#9CA3AF' : '#D32F2F',
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.95rem',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const infoBoxStyle = {
  border: '1px solid #FCD34D',
  background: '#FFFBEB',
  borderRadius: '0.65rem',
  padding: '0.85rem 1rem',
  margin: '0.5rem 0 1.25rem',
  fontSize: '0.88rem',
  color: '#78350F',
  lineHeight: 1.55,
};

export default function SegundaVia() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const formValid =
    fullName.trim().split(/\s+/).filter((t) => t.length >= 2).length >= 2
    && isValidEmail(email)
    && isValidCpf(cpf)
    && stripPhoneDigits(phone).length >= 10;

  const handleSubmitRaw = async (e) => {
    e.preventDefault();
    if (!formValid) return false;
    setError('');

    try {
      const res = await fetch(`${BASE_URL}/patient/segunda-via`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          cpf: cpf.replace(/\D/g, ''),
          phone: stripPhoneDigits(phone),
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 429) {
        setError('Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.');
        return false;
      }

      if (data && data.error === 'invalid_input') {
        setError('Revise os dados informados. Confira nome completo, e-mail, CPF e telefone com DDD.');
        return false;
      }

      // Resposta é genérica (200 { ok: true }) tanto para match quanto para
      // mismatch — anti-enumeração. Sempre mostramos a mesma tela de sucesso.
      if (res.ok) {
        setDone(true);
        return true;
      }

      setError('Não foi possível processar sua solicitação. Tente novamente em alguns minutos.');
      return false;
    } catch {
      setError('Falha de conexão. Verifique sua internet e tente novamente.');
      return false;
    }
  };

  const [submitting, handleSubmit] = useFormSubmitGuard(handleSubmitRaw, { cooldownMs: 4000 });

  if (done) {
    return (
      <>
        <PageSeo
          path="/segunda-via"
          title="Segunda via de documentos — Consulta+"
          description="Solicite a segunda via dos seus documentos médicos emitidos na Consulta+."
          breadcrumbs={[
            { name: 'Início', url: '/' },
            { name: 'Segunda via', url: '/segunda-via' },
          ]}
        />
        <LegalPageShell
          eyebrow="Documentos médicos"
          title="Solicitação recebida"
          subtitle="Verifique seu e-mail em alguns minutos"
          showDivider={false}
        >
          <p>
            Se os dados informados conferirem com o cadastro da sua consulta, os documentos serão
            reenviados <strong>ao e-mail cadastrado</strong> em alguns minutos, com os PDFs originais
            (assinatura ICP-Brasil) em anexo.
          </p>
          <p>
            Verifique também as pastas <strong>spam</strong>, <strong>promoções</strong> e{' '}
            <strong>lixo eletrônico</strong>. Se você tem uma conta Yahoo, procure na aba{' '}
            <em>Notícias</em>.
          </p>
          <p>
            Por segurança e sigilo médico, os documentos <strong>nunca</strong> são enviados a um
            e-mail diferente do informado quando a consulta foi feita — mesmo que você preencha outro
            endereço aqui.
          </p>
          <p style={{ marginBottom: 0 }}>
            Não recebeu, ou precisa atualizar seu e-mail cadastrado?{' '}
            Escreva para <a href="mailto:atendimento@consultamais.app">atendimento@consultamais.app</a>{' '}
            informando nome completo, CPF e o e-mail antigo — nossa equipe faz a verificação manual
            com segurança.
          </p>
        </LegalPageShell>
      </>
    );
  }

  return (
    <>
      <PageSeo
        path="/segunda-via"
        title="Segunda via de documentos — Consulta+"
        description="Reenvie para o seu e-mail cadastrado a segunda via dos documentos médicos emitidos na Consulta+."
        breadcrumbs={[
          { name: 'Início', url: '/' },
          { name: 'Segunda via', url: '/segunda-via' },
        ]}
      />
      <LegalPageShell
        eyebrow="Documentos médicos"
        title="Segunda via de documentos"
        subtitle="Reenvio para o e-mail cadastrado na consulta"
        heroImage="/img/heros/hero-segunda-via-16x9.webp"
        heroAlt="Pintura modernista de um envelope voando como um pássaro carregando um documento com selo dourado"
      >
        <p>
          Use esta página para receber novamente, por e-mail, os documentos médicos já emitidos na
          sua consulta (atestado, receita, encaminhamento ou pedido de exame). Os PDFs originais,
          com assinatura digital <strong>ICP-Brasil</strong>, são reenviados como anexo.
        </p>

        <div style={infoBoxStyle}>
          <strong>Como protegemos o sigilo médico:</strong>
          <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
            <li>Os documentos são reenviados <strong>somente</strong> ao e-mail cadastrado quando a consulta foi feita.</li>
            <li>Você precisa informar exatamente os mesmos <strong>nome, e-mail, CPF e telefone</strong> usados na consulta.</li>
            <li>Se algo não bater, nenhum dado ou documento é revelado — a resposta é sempre a mesma, por segurança.</li>
            <li>Cada cadastro pode solicitar a segunda via no máximo <strong>3 vezes</strong>.</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <label style={labelStyle}>
            Nome completo
            <input
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={(ev) => setFullName(ev.target.value)}
              style={fieldStyle}
              required
            />
          </label>

          <label style={labelStyle}>
            E-mail usado na consulta
            <input
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              style={fieldStyle}
              required
            />
          </label>

          <label style={labelStyle}>
            CPF
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={cpf}
              onChange={(ev) => setCpf(formatCpf(ev.target.value))}
              style={fieldStyle}
              required
            />
          </label>

          <label style={labelStyle}>
            Telefone com DDD
            <input
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
              value={phone}
              onChange={(ev) => setPhone(formatPhoneBR(ev.target.value))}
              style={fieldStyle}
              required
            />
          </label>

          {error ? (
            <p
              role="alert"
              style={{
                margin: '0.5rem 0 0',
                padding: '0.75rem 0.85rem',
                borderRadius: '0.65rem',
                background: '#FEF2F2',
                color: '#991B1B',
                fontSize: '0.88rem',
                lineHeight: 1.5,
              }}
            >
              {error}
            </p>
          ) : null}

          <button type="submit" disabled={!formValid || submitting} style={btnStyle(!formValid || submitting)}>
            {submitting ? 'Enviando…' : 'Reenviar meus documentos'}
          </button>
        </form>
      </LegalPageShell>
    </>
  );
}
